import { useCallback } from 'react'

import { QueryClient, useQuery } from '@tanstack/react-query'

import { API_URL } from '@/constants/env'

import { SweetspotError } from '@/helpers/error'

import { libraryEntityRelationshipsQueryKeys } from '@/hooks/library/types'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { PaginatedResponse } from '@/types/Api'
import type { UUID } from '@/types/Id'
import type { RelationshipWithEntities } from '@/types/library/Entity'

// When load_all=true, each relationship may include from_entity/to_entity

/**
 * Removes deleted entities from all entity relationship queries in the cache.
 * This is used when entities are deleted to keep the UI in sync.
 */
export const removeDeletedEntitiesFromRelationships = (
  queryClient: QueryClient,
  deletedEntityIds: UUID[],
): void => {
  const entityRelationshipsKey = libraryEntityRelationshipsQueryKeys.all()
  const entityRelationshipsQueries = queryClient.getQueriesData<
    PaginatedResponse<RelationshipWithEntities>
  >({
    queryKey: entityRelationshipsKey,
  })

  entityRelationshipsQueries.forEach(([queryKey, queryData]) => {
    if (queryData) {
      const filteredData = queryData.data.filter((relationship) => {
        const toEntityId: UUID | undefined = relationship.to_entity?.id
        const fromEntityId: UUID | undefined = relationship.from_entity?.id
        return (
          (!toEntityId || !deletedEntityIds.includes(toEntityId)) &&
          (!fromEntityId || !deletedEntityIds.includes(fromEntityId))
        )
      })
      queryClient.setQueryData<PaginatedResponse<RelationshipWithEntities>>(
        queryKey,
        {
          ...queryData,
          data: filteredData,
          count: filteredData.length,
        },
      )
    }
  })
}

/**
 * Marks entities as confirmed in all entity relationship queries in the cache.
 * This is used when entities are confirmed to keep the UI in sync.
 */
export const markEntitiesAsConfirmedInRelationships = (
  queryClient: QueryClient,
  confirmedEntityIds: UUID[],
  confirmedByUserId: UUID,
): void => {
  const entityRelationshipsKey = libraryEntityRelationshipsQueryKeys.all()
  const entityRelationshipsQueries = queryClient.getQueriesData<
    PaginatedResponse<RelationshipWithEntities>
  >({
    queryKey: entityRelationshipsKey,
  })

  entityRelationshipsQueries.forEach(([queryKey, queryData]) => {
    if (queryData) {
      const updatedData = queryData.data.map((relationship) => {
        const updatedRelationship = { ...relationship }

        if (
          updatedRelationship.to_entity &&
          updatedRelationship.to_entity.id &&
          confirmedEntityIds.includes(updatedRelationship.to_entity.id)
        ) {
          updatedRelationship.to_entity = {
            ...updatedRelationship.to_entity,
            confirmed: true,
            confirmed_by_user_id: confirmedByUserId,
          }
        }

        if (
          updatedRelationship.from_entity &&
          updatedRelationship.from_entity.id &&
          confirmedEntityIds.includes(updatedRelationship.from_entity.id)
        ) {
          updatedRelationship.from_entity = {
            ...updatedRelationship.from_entity,
            confirmed: true,
            confirmed_by_user_id: confirmedByUserId,
          }
        }

        return updatedRelationship
      })

      queryClient.setQueryData<PaginatedResponse<RelationshipWithEntities>>(
        queryKey,
        {
          ...queryData,
          data: updatedData,
        },
      )
    }
  })
}

export interface UseEntityRelationshipsArgs<T> {
  orgId: UUID | ''
  entityId: UUID | ''
  loadAll?: boolean
  enabled?: boolean
  select?: (data: PaginatedResponse<RelationshipWithEntities>) => T
}

export const fetchEntityRelationships = async (
  orgId: UUID | '',
  entityId: UUID | '',
  accessToken: string,
  loadAll?: boolean,
): Promise<PaginatedResponse<RelationshipWithEntities>> => {
  if (!orgId || !entityId) {
    throw new Error('orgId and entityId are required')
  }

  const url = new URL(
    `${API_URL}/api/v1/organizations/${orgId}/entities/${entityId}/relationships`,
  )
  if (loadAll) url.searchParams.append('load_all', 'true')

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
  })

  if (!response.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: response.statusText,
      cause: response,
    })
  }

  return (await response.json()) as PaginatedResponse<RelationshipWithEntities>
}

export const useEntityRelationships = <T = RelationshipWithEntities[]>({
  orgId,
  entityId,
  loadAll,
  enabled = true,
  select,
}: UseEntityRelationshipsArgs<T>) => {
  const { accessToken } = useAuthInfoWithThrow()

  const selectFn = useCallback(
    (data: PaginatedResponse<RelationshipWithEntities>) => {
      if (select) return select(data)
      return data.data as unknown as T
    },
    [select],
  )

  return useQuery({
    queryKey: libraryEntityRelationshipsQueryKeys.entity({
      orgId,
      entityId,
      loadAll,
    }),
    queryFn: () =>
      fetchEntityRelationships(orgId, entityId, accessToken, loadAll),
    enabled: enabled && !!orgId && !!entityId && !!accessToken,
    staleTime: Infinity,
    select: selectFn,
  })
}

export { libraryEntityRelationshipsQueryKeys } from './types'
